import { Head, router, usePage } from "@inertiajs/react"
import { Edit2, Plus, Trash2 } from "lucide-react"
import { useCallback, useMemo, useRef, useState } from "react"

import { CreateListModal } from "@/components/CreateListModal"
import { DeleteConfirmationModal } from "@/components/DeleteConfirmationModal"
import { EditListModal } from "@/components/EditListModal"
import { EditTaskModal } from "@/components/EditTaskModal"
import { KeyboardShortcutsModal } from "@/components/KeyboardShortcutsModal"
import { LimitReachedModal } from "@/components/LimitReachedModal"
import { NewTaskModal } from "@/components/NewTaskModal"
import TaskList from "@/components/TaskList"
import { Button } from "@/components/ui/button"
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs"
import {
  type KeyboardShortcut,
  useKeyboardShortcuts,
} from "@/hooks/use-keyboard-shortcuts"
import AppLayout from "@/layouts/app-layout"
import { flushListCache, flushListsIndexCache } from "@/lib/cache"
import {
  listPath,
  listTaskArchivalPath,
  listTaskCompletionPath,
  listTaskPath,
  listTasksPath,
  listsPath,
} from "@/routes"
import type { List, Task } from "@/types"

type TaskStatus = "active" | "completed" | "archived"

function isValidStatus(value: string): value is TaskStatus {
  return value === "active" || value === "completed" || value === "archived"
}

interface ListsShowProps {
  list: List
  tasks: Task[]
  status: TaskStatus
  activeTaskCount: number
}

export default function ListsShow({
  list,
  tasks,
  status,
  activeTaskCount,
}: ListsShowProps) {
  const [showNewTaskModal, setShowNewTaskModal] = useState(false)
  const [editingTask, setEditingTask] = useState<Task | null>(null)
  const [showEditListModal, setShowEditListModal] = useState(false)
  const [showDeleteListModal, setShowDeleteListModal] = useState(false)
  const [showDeleteTaskModal, setShowDeleteTaskModal] = useState<Task | null>(
    null,
  )
  const [showHelpModal, setShowHelpModal] = useState(false)
  const [showNewListModal, setShowNewListModal] = useState(false)
  const [showTaskLimitModal, setShowTaskLimitModal] = useState(false)
  const [showListLimitModal, setShowListLimitModal] = useState(false)
  const [inlineTaskDescription, setInlineTaskDescription] = useState("")
  const [isInputFocused, setIsInputFocused] = useState(false)
  const [selectedIndex, setSelectedIndex] = useState(-1)
  const inputRef = useRef<HTMLInputElement>(null)

  const { lists = [] } = usePage().props as { lists?: List[] }

  const MAX_TASKS = 25
  const MAX_LISTS = 9
  const isAtTaskLimit = activeTaskCount >= MAX_TASKS
  const isAtListLimit = lists.length >= MAX_LISTS

  const handleNewTaskClick = useCallback(() => {
    if (isAtTaskLimit) {
      setShowTaskLimitModal(true)
    } else {
      setShowNewTaskModal(true)
    }
  }, [isAtTaskLimit])

  const handleNewListClick = useCallback(() => {
    if (isAtListLimit) {
      setShowListLimitModal(true)
    } else {
      setShowNewListModal(true)
    }
  }, [isAtListLimit])

  const flushCache = useCallback(() => {
    flushListCache(list.id)
    flushListsIndexCache()
  }, [list.id])

  const selectedTask = useMemo(() => {
    if (selectedIndex >= 0 && selectedIndex < tasks.length) {
      return tasks[selectedIndex]
    }
    return null
  }, [selectedIndex, tasks])

  const handleComplete = useCallback(
    (task: Task) => {
      if (task.status === "completed") {
        router.delete(listTaskCompletionPath(list.id, task.id), {
          preserveScroll: true,
          onSuccess: flushCache,
        })
      } else {
        router.post(
          listTaskCompletionPath(list.id, task.id),
          {},
          {
            preserveScroll: true,
            onSuccess: flushCache,
          },
        )
      }
    },
    [list.id, flushCache],
  )

  const handleArchive = useCallback(
    (task: Task) => {
      router.post(
        listTaskArchivalPath(list.id, task.id),
        {},
        {
          preserveScroll: true,
          onSuccess: flushCache,
        },
      )
    },
    [list.id, flushCache],
  )

  const handleUnarchive = useCallback(
    (task: Task) => {
      router.delete(listTaskArchivalPath(list.id, task.id), {
        preserveScroll: true,
        onSuccess: flushCache,
      })
    },
    [list.id, flushCache],
  )

  const jumpToList = useCallback(
    (index: number) => {
      if (index < lists.length) {
        router.visit(listPath(lists[index].id))
      }
    },
    [lists],
  )

  const handleStatusChange = useCallback(
    (newStatus: string) => {
      if (!isValidStatus(newStatus)) return
      setSelectedIndex(-1)
      if (newStatus === "active") {
        router.visit(listPath(list.id))
      } else {
        router.visit(listPath(list.id, { status: newStatus }))
      }
    },
    [list.id],
  )

  const handleDeleteTask = useCallback(
    (task: Task) => {
      router.delete(listTaskPath(list.id, task.id, { status }), {
        preserveScroll: true,
        onSuccess: () => {
          setShowDeleteTaskModal(null)
        },
      })
    },
    [list.id, status],
  )

  const shortcuts: KeyboardShortcut[] = useMemo(
    () => [
      {
        key: "?",
        handler: () => setShowHelpModal(true),
        description: "Show keyboard shortcuts",
        category: "global",
      },
      {
        key: "n",
        handler: () => {
          if (status === "active") {
            inputRef.current?.focus()
          }
        },
        description: "New task",
        category: "actions",
        when: () => status === "active",
      },
      {
        key: "N",
        modifiers: { shift: true },
        handler: handleNewTaskClick,
        description: "New task (modal)",
        category: "actions",
        when: () => status === "active",
      },
      {
        key: "n",
        handler: handleNewTaskClick,
        description: "New task",
        category: "actions",
        when: () => status !== "active",
      },
      {
        key: "k",
        handler: () => {
          if (tasks.length === 0) return
          setSelectedIndex((prev) => {
            if (prev <= 0) return tasks.length - 1
            return prev - 1
          })
        },
        description: "Previous task",
        category: "navigation",
        when: () => tasks.length > 0,
      },
      {
        key: "j",
        handler: () => {
          if (tasks.length === 0) return
          setSelectedIndex((prev) => {
            if (prev < 0 || prev >= tasks.length - 1) return 0
            return prev + 1
          })
        },
        description: "Next task",
        category: "navigation",
        when: () => tasks.length > 0,
      },
      // Tab navigation
      {
        key: "a",
        handler: () => {
          if (status !== "active") {
            handleStatusChange("active")
          }
        },
        description: "Switch to Alive",
        category: "navigation",
        when: () => status !== "active",
      },
      {
        key: "D",
        modifiers: { shift: true },
        handler: () => {
          if (status !== "completed") {
            handleStatusChange("completed")
          }
        },
        description: "Switch to Done",
        category: "navigation",
        when: () => status !== "completed",
      },
      {
        key: "g",
        handler: () => {
          if (status !== "archived") {
            handleStatusChange("archived")
          }
        },
        description: "Switch to Dead",
        category: "navigation",
        when: () => status !== "archived",
      },
      {
        key: "h",
        handler: () => router.visit(listsPath()),
        description: "Go to Home",
        category: "navigation",
      },
      // Actions - context-dependent
      {
        key: "x",
        handler: () => {
          if (!selectedTask) return
          handleComplete(selectedTask)
        },
        description:
          status === "completed" ? "Undo completion" : "Complete task",
        category: "actions",
        when: () =>
          selectedTask !== null &&
          (status === "active" || status === "completed"),
      },
      {
        key: "d",
        handler: () => {
          if (selectedTask) {
            setShowDeleteTaskModal(selectedTask)
          }
        },
        description: "Delete task",
        category: "actions",
        when: () => selectedTask !== null,
      },
      {
        key: "A",
        modifiers: { shift: true },
        handler: () => {
          if (selectedTask) {
            handleArchive(selectedTask)
          }
        },
        description: "Archive task",
        category: "actions",
        when: () => status === "completed" && selectedTask !== null,
      },
      {
        key: "u",
        handler: () => {
          if (selectedTask) {
            handleUnarchive(selectedTask)
          }
        },
        description: "Unarchive task",
        category: "actions",
        when: () => status === "archived" && selectedTask !== null,
      },
      {
        key: "Enter",
        handler: () => {
          if (selectedTask) {
            setEditingTask(selectedTask)
          }
        },
        description: "Edit task",
        category: "actions",
        when: () => selectedTask !== null,
      },
      {
        key: "e",
        handler: () => {
          if (selectedTask) {
            setEditingTask(selectedTask)
          }
        },
        description: "Edit task",
        category: "actions",
        when: () => selectedTask !== null,
      },
      {
        key: "Escape",
        handler: () => setSelectedIndex(-1),
        description: "Clear selection",
        category: "navigation",
        when: () => selectedIndex >= 0,
      },
      {
        key: "l",
        handler: handleNewListClick,
        description: "New list",
        category: "actions",
      },
      ...Array.from({ length: 9 }, (_, i) => ({
        key: String(i + 1),
        handler: () => jumpToList(i),
        description: `Jump to list ${i + 1}`,
        category: "navigation" as const,
        when: () => lists.length > i,
      })),
    ],
    [
      status,
      tasks.length,
      selectedTask,
      selectedIndex,
      handleComplete,
      handleArchive,
      handleUnarchive,
      handleStatusChange,
      handleNewTaskClick,
      handleNewListClick,
      jumpToList,
      lists.length,
    ],
  )

  useKeyboardShortcuts({ shortcuts })

  // Derive visible shortcuts from the shortcuts array
  // This prevents drift between shortcuts and visibleShortcuts
  const visibleShortcuts = useMemo((): KeyboardShortcut[] => {
    const result: KeyboardShortcut[] = []

    // Build a set of keys to hide based on current state
    const hideKeys = new Set<string>()

    // Hide number keys - we'll add a combined "1-9" entry instead
    for (let i = 1; i <= 9; i++) hideKeys.add(String(i))

    // Hide "N" (shift+n for modal) when not on active tab - lowercase n now works on all tabs
    if (status !== "active") {
      hideKeys.add("N")
    }

    // Hide j/k navigation when no tasks
    if (tasks.length === 0) {
      hideKeys.add("j")
      hideKeys.add("k")
    }

    // Hide tab navigation for current tab
    if (status === "active") hideKeys.add("a")
    if (status === "completed") hideKeys.add("D")
    if (status === "archived") hideKeys.add("g")

    // Hide task actions when no task selected
    if (selectedTask === null) {
      hideKeys.add("x")
      hideKeys.add("d")
      hideKeys.add("A")
      hideKeys.add("u")
      hideKeys.add("Enter")
      hideKeys.add("e")
    }

    // Hide Escape when nothing selected
    if (selectedIndex < 0) hideKeys.add("Escape")

    // Hide archive when not on completed tab
    if (status !== "completed") hideKeys.add("A")

    // Hide unarchive when not on archived tab
    if (status !== "archived") hideKeys.add("u")

    // Hide x (complete/undo) when on archived tab
    if (status === "archived") hideKeys.add("x")

    // eslint-disable-next-line react-hooks/refs -- shortcuts is an array, not accessing ref values
    for (const shortcut of shortcuts) {
      const keyId = shortcut.modifiers?.shift
        ? shortcut.key.toUpperCase()
        : shortcut.key
      if (hideKeys.has(keyId)) continue

      // Strip handler and when for display
      result.push({
        key: shortcut.key,
        modifiers: shortcut.modifiers,
        description: shortcut.description,
        category: shortcut.category,
      })
    }

    // Add combined "1-9" entry if any lists exist
    if (lists.length > 0) {
      result.push({
        key: "1-9",
        description: "Jump to list",
        category: "navigation",
      })
    }

    return result
  }, [
    shortcuts,
    status,
    tasks.length,
    selectedTask,
    selectedIndex,
    lists.length,
  ])

  const handleInlineTaskSubmit = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && inlineTaskDescription.trim()) {
      e.preventDefault()
      if (isAtTaskLimit) {
        setShowTaskLimitModal(true)
        return
      }
      router.post(
        listTasksPath(list.id),
        {
          task: { description: inlineTaskDescription.trim() },
        },
        {
          preserveScroll: true,
          onSuccess: () => setInlineTaskDescription(""),
        },
      )
    }
  }

  const handleDeleteList = () => {
    router.delete(listPath(list.id), {
      onSuccess: () => {
        setShowDeleteListModal(false)
      },
    })
  }

  const activeCount = status === "active" ? tasks.length : 0

  return (
    <>
      <Head title={list.name} />
      <AppLayout breadcrumbs={[{ label: list.name }]}>
        <div className="w-full max-w-[600px] space-y-4">
          <div className="flex items-center justify-between gap-4">
            <Tabs value={status} onValueChange={handleStatusChange}>
              <TabsList>
                <TabsTrigger value="active">
                  Alive
                  {status === "active" && activeCount > 0 && (
                    <span className="bg-red ml-2 rounded-full px-2 py-0.5 font-mono text-xs text-white">
                      {activeCount}
                    </span>
                  )}
                </TabsTrigger>
                <TabsTrigger value="completed">Done</TabsTrigger>
                <TabsTrigger value="archived">Dead</TabsTrigger>
              </TabsList>
            </Tabs>

            <div className="flex gap-1">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setShowEditListModal(true)}
                title="Edit list"
              >
                <Edit2 className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setShowDeleteListModal(true)}
                title="Delete list"
                className="text-destructive hover:text-destructive"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {status === "active" && (
            <div
              className={`bg-bg-input flex items-center gap-3 rounded-lg px-4 py-3 transition-all duration-200 ${
                isInputFocused
                  ? "ring-red/50 shadow-red-glow ring-2"
                  : "hover:bg-bg-hover"
              }`}
            >
              <Plus
                className={`h-5 w-5 shrink-0 transition-colors ${
                  isInputFocused ? "text-red-bright" : "text-bone-muted"
                }`}
              />
              <input
                ref={inputRef}
                type="text"
                value={inlineTaskDescription}
                onChange={(e) => setInlineTaskDescription(e.target.value)}
                onKeyDown={handleInlineTaskSubmit}
                onFocus={() => setIsInputFocused(true)}
                onBlur={() => setIsInputFocused(false)}
                placeholder="Add a task..."
                className="placeholder:text-bone-muted w-full border-none bg-transparent text-sm shadow-none ring-0 outline-none focus:border-none focus:shadow-none focus:ring-0 focus:ring-transparent focus:outline-none"
              />
              {!isInputFocused && !inlineTaskDescription && (
                <span className="bg-bg-hover text-bone-muted shrink-0 rounded px-2 py-0.5 font-mono text-xs">
                  N
                </span>
              )}
            </div>
          )}

          <TaskList
            tasks={tasks}
            listId={list.id}
            enableDragDrop={status === "active"}
            onTaskClick={setEditingTask}
            onTaskDelete={setShowDeleteTaskModal}
            onAddTask={handleNewTaskClick}
            autoArchiveDays={list.auto_archive_days}
            status={status}
            selectedIndex={selectedIndex}
          />
        </div>

        <NewTaskModal
          open={showNewTaskModal}
          onOpenChange={setShowNewTaskModal}
          listId={list.id}
          status={status}
        />

        <EditTaskModal
          task={editingTask}
          onClose={() => setEditingTask(null)}
          listId={list.id}
          autoArchiveDays={list.auto_archive_days}
        />

        <EditListModal
          open={showEditListModal}
          onOpenChange={setShowEditListModal}
          list={list}
        />

        <DeleteConfirmationModal
          open={showDeleteListModal}
          onOpenChange={setShowDeleteListModal}
          onConfirm={handleDeleteList}
          title="Delete List"
          description={`Are you sure you want to delete "${list.name}"? This will permanently delete the list and all its tasks.`}
          confirmText="Delete List"
        />

        <DeleteConfirmationModal
          open={!!showDeleteTaskModal}
          onOpenChange={(open) => !open && setShowDeleteTaskModal(null)}
          onConfirm={() =>
            showDeleteTaskModal && handleDeleteTask(showDeleteTaskModal)
          }
          title="Delete Task"
          description={`Are you sure you want to delete "${showDeleteTaskModal?.description}"? This action cannot be undone.`}
          confirmText="Delete Task"
        />

        <KeyboardShortcutsModal
          open={showHelpModal}
          onOpenChange={setShowHelpModal}
          shortcuts={visibleShortcuts}
        />

        <CreateListModal
          open={showNewListModal}
          onOpenChange={setShowNewListModal}
        />

        <LimitReachedModal
          open={showTaskLimitModal}
          onOpenChange={setShowTaskLimitModal}
          title="Task Limit Reached"
          description="This list has 25 active tasks. Finish something before adding more — we believe in you."
        />

        <LimitReachedModal
          open={showListLimitModal}
          onOpenChange={setShowListLimitModal}
          title="List Limit Reached"
          description="You've hit the list limit. Nine lists is plenty — any more and you'd need a list of lists."
        />
      </AppLayout>
    </>
  )
}
